import { Component, inject, OnInit, signal } from '@angular/core';
import {
  AbstractControl,
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import {
  AlertController,
  IonButton,
  IonButtons,
  IonCol,
  IonContent,
  IonFooter,
  IonGrid,
  IonHeader,
  IonIcon,
  IonInput,
  IonRow,
  IonTitle,
  IonToolbar,
  LoadingController,
  ModalController,
} from '@ionic/angular/standalone';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { FileDropAreaComponent } from '@okcargo-apps/layout-ionic';
import { EMessageLevel, MessagesUiService } from '@okcargo-apps/messages-ui';
import { EDocumentType } from '@okcargo-apps/shared-domain';
import { JpgToPdfService } from '@okcargo-apps/shared-infrastructure-ionic';
import { addDays, format } from 'date-fns';
import { isValidIBAN } from 'ibantools';
import { CompanyService } from '../company.service';

@Component({
  selector: 'lib-upload-document-modal',
  templateUrl: './upload-document-modal.component.html',
  styleUrls: ['./upload-document-modal.component.scss'],
  imports: [
    IonInput,
    IonFooter,
    IonRow,
    IonGrid,
    IonContent,
    IonCol,
    IonTitle,
    IonIcon,
    IonButton,
    IonButtons,
    IonToolbar,
    IonHeader,
    TranslateModule,
    FileDropAreaComponent,
    ReactiveFormsModule,
  ],
})
export class UploadDocumentModalComponent implements OnInit {
  private readonly fb = inject(FormBuilder);
  private readonly modalCtrl = inject(ModalController);
  private readonly loadingCtrl = inject(LoadingController);
  private readonly alertCtrl = inject(AlertController);
  private readonly translate = inject(TranslateService);
  private readonly companyService = inject(CompanyService);
  private readonly jpgToPdfService = inject(JpgToPdfService);
  private readonly messagesUiService = inject(MessagesUiService);

  public documentType!: EDocumentType;

  public expirationText: string = this.translate.instant('company.valid_12_m');
  public expirationTextError: string =
    this.translate.instant('error.invalid_date') + ' ' + this.translate.instant('company.valid_12_m');

  public metadataDocumentForm: FormGroup;

  public EDocumentType = EDocumentType;

  private selectedDocument: File | string | null = null;

  public errorMessage = signal<string | null>(null);

  constructor() {
    // const tomorrow = addDays(new Date(), 1); // Add 1 day to today
    const endDate = addDays(new Date(), 15); // Add 15 day to today

    this.metadataDocumentForm = this.fb.group({
      iban: ['', []],
      assuranceQuantity: ['', []],
      expirationDate: [format(endDate, 'yyyy-MM-dd'), []],
    });
  }

  ngOnInit(): void {
    this.updateForm();
  }

  private updateForm(): void {
    switch (this.documentType) {
      case EDocumentType.BANK_OWNERSHIP_CERTIFICATE:
        this.metadataDocumentForm.get('iban')?.setValidators([Validators.required, this.ibanValidator()]);
        this.metadataDocumentForm.get('expirationDate')?.clearValidators();
        this.metadataDocumentForm.get('assuranceQuantity')?.clearValidators();
        break;

      case EDocumentType.TAX_AUTHORITIES_PAYMENT_CERTIFICATE:
        this.metadataDocumentForm.get('expirationDate')?.setValidators([Validators.required, this.dateWithinRange(12)]);
        this.metadataDocumentForm.get('iban')?.clearValidators();
        this.metadataDocumentForm.get('assuranceQuantity')?.clearValidators();
        break;

      case EDocumentType.SOCIAL_SECURITY_PAYMENT_CERTIFICATE:
        this.expirationText = this.translate.instant('company.valid_1_m');
        this.expirationTextError =
          this.translate.instant('error.invalid_date') + ' ' + this.translate.instant('company.valid_1_m');
        this.metadataDocumentForm.get('expirationDate')?.setValidators([Validators.required, this.dateWithinRange(1)]);
        this.metadataDocumentForm.get('iban')?.clearValidators();
        this.metadataDocumentForm.get('assuranceQuantity')?.clearValidators();
        break;

      case EDocumentType.GOODS_ASSURANCE:
        this.metadataDocumentForm.get('expirationDate')?.setValidators([Validators.required, this.dateWithinRange(12)]);
        this.metadataDocumentForm
          .get('assuranceQuantity')
          ?.setValidators([Validators.required, Validators.min(150000)]);
        this.metadataDocumentForm.get('iban')?.clearValidators();
        break;

      case EDocumentType.EUROPEAN_LICENSE:
      case EDocumentType.CIVIL_RESPONSABILITY_ASSURANCE:
        this.metadataDocumentForm.get('expirationDate')?.setValidators([Validators.required, this.dateWithinRange(12)]);
        this.metadataDocumentForm.get('iban')?.clearValidators();
        this.metadataDocumentForm.get('assuranceQuantity')?.clearValidators();
        break;

      default:
        break;
    }

    this.metadataDocumentForm.get('assuranceQuantity')?.updateValueAndValidity();
    this.metadataDocumentForm.get('iban')?.updateValueAndValidity();
    this.metadataDocumentForm.get('expirationDate')?.updateValueAndValidity();
  }

  public filterInputIban(event: any): void {
    let inputValue = event.target.value;
    inputValue = inputValue.replace(/[^a-zA-Z0-9\s-]/g, '');
    this.metadataDocumentForm.get('iban')?.setValue(inputValue, { emitEvent: false });
  }

  private formatIban(): string {
    let iban = this.metadataDocumentForm.get('iban')?.value;
    if (iban) {
      iban = iban.replace(/[\s-]/g, '').toUpperCase();
    }
    return iban || '';
  }

  private ibanValidator(): ValidatorFn {
    return (): { [key: string]: any } | null => {
      const iban = this.formatIban();
      if (!isValidIBAN(iban)) {
        return { invalidIban: true };
      }
      return null;
    };
  }

  private dateWithinRange(months: number): ValidatorFn {
    // TODO use date-fns???
    return (control: AbstractControl): ValidationErrors | null => {
      const currentDate = new Date();
      const expirationDate = new Date(control.value);

      if (isNaN(expirationDate.getTime())) {
        return { invalidDate: true };
      }

      const maxDate = new Date();
      maxDate.setMonth(currentDate.getMonth() + months);

      return expirationDate >= currentDate && expirationDate <= maxDate ? null : { dateOutOfRange: true };
    };
  }

  public async uploadCompanyDocumentAsFile(file: File): Promise<void> {
    this.selectedDocument = file;
  }

  public async uploadCompanyDocumentAsImage(image: string): Promise<void> {
    const file = await this.jpgToPdfService.convertBase64ImageToPdf(image);
    // this.selectedDocument = image;
    this.selectedDocument = file;
  }

  public async confirm(): Promise<void> {
    if (this.metadataDocumentForm.valid && this.selectedDocument) {
      const loading = await this.loadingCtrl.create({
        message: this.translate.instant('general.send_data'),
      });
      await loading.present();

      const ibanWhithFormat = this.formatIban();
      this.metadataDocumentForm.get('iban')?.setValue(ibanWhithFormat);
      const formData = this.metadataDocumentForm.value;

      try {
        const result = await this.companyService.uploadCompanyDocument(
          formData,
          this.selectedDocument,
          this.documentType,
        );
        this.messagesUiService.setMessage({
          objectId: 'companyDocs',
          message: [this.translate.instant('company.uploaded_document')],
          level: EMessageLevel.INFO,
        });
        await this.modalCtrl.dismiss(result, 'confirm');
      } catch (error) {
        this.errorMessage.set(this.translate.instant('error.send_form'));
      } finally {
        await loading.dismiss();
      }
    } else {
      // console.error('Formulario no válido o archivo no seleccionado.', this.metadataDocumentForm.valid,  this.selectedDocument);
      this.errorMessage.set(this.translate.instant('error.empty_file_or_form'));
    }
  }

  public async closeModalWithCancel(): Promise<boolean> {
    if (this.selectedDocument) {
      const alert = await this.alertCtrl.create({
        header: this.translate.instant('general.confirm'),
        message: this.translate.instant('general.close_without_save_msg'),
        buttons: [
          {
            text: this.translate.instant('general.cancel'),
            role: 'cancel',
            handler: () => {
              // do nothing
            },
          },
          {
            text: this.translate.instant('general.close_without_save'),
            role: 'confirm',
            handler: async () => {
              await this.modalCtrl.dismiss(null, 'cancel');
            },
          },
        ],
      });
      await alert.present();
      return false;
    } else {
      return this.modalCtrl.dismiss(null, 'cancel');
    }
  }
}
